import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { api } from "@/lib/api";

function diffParts(target) {
  const now = Date.now();
  const ms = target - now;
  if (ms <= 0) return null;
  const sec = Math.floor(ms / 1000) % 60;
  const min = Math.floor(ms / 1000 / 60) % 60;
  const hr = Math.floor(ms / 1000 / 60 / 60) % 24;
  const day = Math.floor(ms / 1000 / 60 / 60 / 24);
  return { day, hr, min, sec };
}

const pad = (n) => String(n).padStart(2, "0");

export default function CountdownTimer({ joinHref = "/#drop-list", compact = false }) {
  const [launchAt, setLaunchAt] = useState(null);
  const [parts, setParts] = useState(null);
  const [expired, setExpired] = useState(false);

  useEffect(() => {
    let mounted = true;
    api
      .get("/drops/001")
      .then((r) => {
        if (!mounted) return;
        const ts = new Date(r.data.launch_at).getTime();
        setLaunchAt(ts);
      })
      .catch(() => setExpired(false));
    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    if (!launchAt) return;
    function tick() {
      const p = diffParts(launchAt);
      if (!p) {
        setExpired(true);
        setParts(null);
      } else {
        setExpired(false);
        setParts(p);
      }
    }
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [launchAt]);

  return (
    <div
      data-testid="countdown-timer"
      className={`relative border border-[#262626] bg-black/70 backdrop-blur-sm ${
        compact ? "p-4 md:p-5" : "p-5 md:p-7"
      } shadow-[0_0_30px_rgba(255,20,147,0.12)] overflow-hidden`}
    >
      {/* Glow accents */}
      <div className="absolute -top-16 -left-16 w-48 h-48 rounded-full bg-[#ff1493] opacity-[0.10] blur-3xl pointer-events-none" />
      <div className="absolute -bottom-20 -right-20 w-56 h-56 rounded-full bg-[#9d4cdd] opacity-[0.10] blur-3xl pointer-events-none" />

      <div className="relative flex flex-col gap-3 md:gap-4">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-2">
            <span className="w-1.5 h-1.5 rounded-full bg-[#ff1493] shadow-[0_0_8px_#ff1493] animate-pulse" />
            <span className="font-mono text-[10px] md:text-[11px] tracking-[0.25em] uppercase text-[#ff1493]">
              // Drop 001 Access Window
            </span>
          </div>
          <span className="font-mono text-[10px] tracking-[0.2em] uppercase text-[#6b6b6b]">
            {expired ? "Status: Opening" : "Status: Sealed"}
          </span>
        </div>

        {expired ? (
          <div
            data-testid="countdown-expired"
            className={`font-display ${compact ? "text-2xl md:text-3xl" : "text-3xl md:text-5xl"} tracking-tight leading-[0.95]`}
          >
            <span className="text-[#ff1493]">Access window</span> opening soon.
          </div>
        ) : parts ? (
          <div
            data-testid="countdown-digits"
            className="grid grid-cols-4 gap-2 md:gap-3"
          >
            {[
              ["D", parts.day],
              ["H", parts.hr],
              ["M", parts.min],
              ["S", parts.sec],
            ].map(([label, val], i) => (
              <div
                key={label}
                className="border border-[#1a1a1a] bg-[#050505] py-3 md:py-4 px-2 text-center"
              >
                <div
                  className={`font-display ${
                    compact ? "text-2xl md:text-4xl" : "text-3xl md:text-5xl lg:text-6xl"
                  } tracking-tighter leading-none tabular-nums`}
                  style={i === 0 ? { color: "#fff" } : { color: "#ffffff" }}
                >
                  {label === "D" ? val : pad(val)}
                </div>
                <div className="mt-1.5 font-mono text-[10px] tracking-[0.2em] uppercase text-[#ff1493]">
                  {label === "D" ? "Days" : label === "H" ? "Hrs" : label === "M" ? "Min" : "Sec"}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="font-mono text-[11px] tracking-[0.2em] uppercase text-[#6b6b6b]">
            // syncing timestamp...
          </div>
        )}

        <p className="font-body text-xs md:text-sm text-[#a3a3a3] leading-relaxed">
          Preorder access unlocks when the file opens.
        </p>

        <div>
          {joinHref.startsWith("/") && !joinHref.startsWith("/#") ? (
            <Link to={joinHref} data-testid="countdown-join-cta" className="btn-primary">
              Join Drop List <ArrowRight size={14} strokeWidth={1.5} />
            </Link>
          ) : (
            <a href={joinHref} data-testid="countdown-join-cta" className="btn-primary">
              Join Drop List <ArrowRight size={14} strokeWidth={1.5} />
            </a>
          )}
        </div>
      </div>
    </div>
  );
}
